import * as Sentry from '@sentry/react';
import { Schema } from 'effect';
import { AppError, ErrorCode, ErrorDomain } from '../../types/errors/errors';
import {
  Connected,
  Connecting,
  ConnectionError,
  Disconnected,
  type ConnectionState
} from '../../types/general/connectionState';
import type { JrpcNotification } from '../../types/general/jrpc';
import { createLogger, type Logger } from '../../utils/logger';
import { JrpcClient } from '../jrpc/JrpcClient';
import { GcsWebSocketClosureCodes } from './GcsWebSocketClosureCodes';

const HEALTH_CHECK_TIMEOUT_MS = (() => {
  const timeout = import.meta.env.VITE_GCS_PING_TIMEOUT_MS;
  if (timeout === undefined) return undefined;
  const parsedTimeout = Number(timeout);
  return Number.isFinite(parsedTimeout) ? parsedTimeout : undefined;
})();
const HEALTH_CHECK_QUERY_PARAM = 'health-check';
const HEALTH_CHECK_ENABLED = HEALTH_CHECK_TIMEOUT_MS !== undefined && HEALTH_CHECK_TIMEOUT_MS > 0;

type NotificationHandler<T> = (data: T) => void;
type StateChangeHandler = (state: ConnectionState, error?: string) => void;

interface NotificationSubscription<D, E> {
  schema: Schema.Schema<D, E, never>;
  handler: NotificationHandler<D>;
}

export class GcsWebSocket {
  private static readonly HEALTH_CHECK_TOPIC = 'health_check';
  private static readonly HEALTH_CHECK_KEEP_ALIVE = `${GcsWebSocket.HEALTH_CHECK_TOPIC}.keep_alive`;

  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly stateChangeHandlers = new Set<StateChangeHandler>();
  private readonly jrpcClient: JrpcClient;
  private readonly logger: Logger;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly notificationHandlers = new Map<string, Set<NotificationSubscription<any, any>>>();
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private hasEverConnected = false;
  private _state: ConnectionState = new Disconnected();
  private healthCheckTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, tag: string = '') {
    this.url = url;
    this.logger = createLogger(tag || 'GcsWebSocket');

    this.jrpcClient = new JrpcClient(
      tag,
      (payload) => this.send(payload),
      (notification) => this.handleNotification(notification)
    );
  }

  get state(): ConnectionState {
    return this._state;
  }

  connect(queryParams?: Record<string, string>): void {
    if (this.ws?.readyState === WebSocket.OPEN || this._state instanceof Connected) {
      this.logger.warn('WebSocket already connected');
      return;
    }

    this.setState(new Connecting());
    const finalUrl = this.buildFinalUrl(
      HEALTH_CHECK_ENABLED ? { ...queryParams, [HEALTH_CHECK_QUERY_PARAM]: 'true' } : queryParams
    );
    this.logger.info(`Connecting to: ${finalUrl}`);

    try {
      this.ws = new WebSocket(finalUrl);

      this.ws.onopen = () => {
        const isFirstConnection = !this.hasEverConnected;
        if (this.reconnectAttempts > 0) {
          this.logger.info(`Reconnected successfully after ${this.reconnectAttempts} attempt(s)`);
        } else {
          this.logger.info('Connected successfully');
        }

        this.hasEverConnected = true;
        this.reconnectAttempts = 0;
        this.setState(new Connected(isFirstConnection));
        this.resetHealthCheckTimeout();
      };

      this.ws.onclose = (event) => {
        this.handleSocketDisconnect(event.code, event.reason);
      };

      this.ws.onerror = () => {
        this.setState(new ConnectionError(), 'WebSocket error');
      };

      this.ws.onmessage = (event) => {
        this.resetHealthCheckTimeout();
        this.jrpcClient.handleMessage(event.data);
      };
    } catch (error) {
      this.setState(new ConnectionError(), error instanceof Error ? error.message : 'Connection failed');
    }
  }

  disconnect(): void {
    if (this._state instanceof Disconnected) {
      this.logger.warn('WebSocket already disconnected');
      return;
    }

    this.logger.info('Manual disconnect requested');
    this.clearHealthCheckTimeout();
    this.reconnectAttempts = Infinity;
    this.ws?.close();
    this.ws = null;
  }

  addStateChangeHandler(handler: (state: ConnectionState, error?: string) => void): () => void {
    this.stateChangeHandlers.add(handler);
    return () => this.stateChangeHandlers.delete(handler);
  }

  async call<ID, IE, OD, OE>(
    method: string,
    schemaI: Schema.Schema<ID, IE, never>,
    schemaO: Schema.Schema<OD, OE, never>,
    input: ID
  ): Promise<OD> {
    const params = input ? [Schema.encodeSync(schemaI)(input)] : [];
    const response = await this.jrpcClient.call(method, params);
    if (response.error) {
      throw new AppError({
        domain: ErrorDomain.JRPC,
        code: ErrorCode.JRPC_RESPONSE_ERROR,
        params: {
          method,
          errorMessage: response.error.message,
          errorCode: response.error.code
        }
      });
    }

    return Schema.decodeUnknownSync(schemaO)(response.result);
  }

  addNotificationHandler<D, E>(
    method: string,
    schema: Schema.Schema<D, E, never>,
    handler: NotificationHandler<D>
  ): () => void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const methodHandlers = this.notificationHandlers.get(method) ?? new Set<NotificationSubscription<any, any>>();
    const subscription = {
      schema,
      handler
    } as NotificationSubscription<D, E>;

    methodHandlers.add(subscription);
    this.notificationHandlers.set(method, methodHandlers);

    return () => {
      const currentMethodHandlers = this.notificationHandlers.get(method);
      if (!currentMethodHandlers) {
        return;
      }

      currentMethodHandlers.delete(subscription);
      if (currentMethodHandlers.size === 0) {
        this.notificationHandlers.delete(method);
      }
    };
  }

  private handleNotification(notification: JrpcNotification): void {
    if (notification.method === GcsWebSocket.HEALTH_CHECK_KEEP_ALIVE) {
      return;
    }

    const handlers = this.notificationHandlers.get(notification.method);
    if (!handlers || handlers.size === 0) {
      return;
    }

    const payload = this.extractNotificationPayload(notification.params);

    handlers.forEach(({ schema, handler }) => {
      try {
        const parsedData = Schema.decodeUnknownSync(schema)(payload);
        handler(parsedData);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            method: notification.method,
            payload,
            url: this.url
          }
        });

        this.logger.error(`Failed to handle notification ${notification.method}`, {
          error,
          payload
        });
      }
    });
  }

  private extractNotificationPayload(params: unknown): unknown {
    if (!Array.isArray(params)) {
      return params;
    }

    if (params.length === 1) {
      return params[0];
    }

    return params;
  }

  private send(payload: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    this.ws.send(payload);
  }

  private setState(state: ConnectionState, error?: string): void {
    this._state = state;
    this.stateChangeHandlers.forEach((handler) => handler(state, error));
  }

  private buildFinalUrl(queryParams?: Record<string, string>): string {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return this.url;
    }

    const urlObj = new URL(this.url);
    Object.entries(queryParams).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value);
    });

    return urlObj.toString();
  }

  private shouldReconnect(code: number): boolean {
    const reconnectCodes: number[] = [
      GcsWebSocketClosureCodes.GOING_AWAY,
      GcsWebSocketClosureCodes.NO_STATUS_RECEIVED,
      GcsWebSocketClosureCodes.ABNORMAL_CLOSURE,
      GcsWebSocketClosureCodes.INTERNAL_ERROR,
      GcsWebSocketClosureCodes.SERVICE_RESTART,
      GcsWebSocketClosureCodes.TRY_AGAIN_LATER,
      GcsWebSocketClosureCodes.BAD_GATEWAY
    ];

    if (reconnectCodes.includes(code)) {
      return true;
    }

    if (code >= 4100 && code <= 4199) {
      return true;
    }

    return false;
  }

  private handleSocketDisconnect(code: number, reason: string): void {
    this.clearHealthCheckTimeout();
    this.ws = null;
    this.logger.info(`Disconnected: code=${code}, reason=${reason}`);
    this.jrpcClient.handleDisconnect();
    this.notificationHandlers.clear();

    if (this.shouldReportDisconnection(code)) {
      this.reportDisconnection(code, reason);
    }

    const shouldAttemptReconnect = this.shouldReconnect(code);

    if (shouldAttemptReconnect && !isFinite(this.reconnectAttempts)) {
      return;
    }

    if (shouldAttemptReconnect) {
      this.reconnectAttempts += 1;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      this.logger.info(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
      this.setState(new Connecting());
      setTimeout(() => this.connect(), delay);
      return;
    }

    this.logger.info(`Not reconnecting due to close code ${code}`);
    this.setState(new Disconnected(code, reason));
  }

  private resetHealthCheckTimeout(): void {
    if (!HEALTH_CHECK_ENABLED) return;

    this.clearHealthCheckTimeout();
    this.healthCheckTimer = setTimeout(() => {
      this.healthCheckTimer = null;
      this.logger.warn(`No health check received within ${HEALTH_CHECK_TIMEOUT_MS}ms, declaring connection dead`);
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close(GcsWebSocketClosureCodes.OTHER_RECONNECT);
      }
      this.handleSocketDisconnect(GcsWebSocketClosureCodes.OTHER_RECONNECT, 'Health check timeout');
    }, HEALTH_CHECK_TIMEOUT_MS);
  }

  private clearHealthCheckTimeout(): void {
    if (this.healthCheckTimer !== null) {
      clearTimeout(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  private shouldReportDisconnection(code: number): boolean {
    if (this.reconnectAttempts > 5) return false;
    return (
      code !== GcsWebSocketClosureCodes.NORMAL_CLOSURE &&
      code !== GcsWebSocketClosureCodes.NO_STATUS_RECEIVED &&
      code !== GcsWebSocketClosureCodes.USER_LOGGED_OUT
    );
  }

  private reportDisconnection(code: number, reason: string): void {
    const error = new AppError({
      domain: ErrorDomain.WEBSOCKET,
      code: ErrorCode.ABNORMAL_WEBSOCKET_CLOSURE
    });

    Sentry.captureException(error, {
      extra: {
        code,
        reason,
        url: this.url
      }
    });
  }
}
